import { useMemo } from "react";

import { Stack, Typography } from "@mui/material";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { EMPTY_VALUE_PLACEHOLDER, formatShortDate, formatTime } from "@shared";

import { AppointmentExchangeHistoryEntryResponse } from "@/api";
import { AppointmentPurposeChip } from "@/common/components/appointments/shared/components/appointment-purpose-chip/AppointmentPurposeChip";
import { ColumnFilterHeader, ColumnFilterOption } from "@/common/components/table/ColumnFilterHeader";
import { useLanguageContext } from "@/common/contexts/language-context/LanguageContext";
import { locales } from "@/common/contexts/localization-date-context/LocalizationDateContextProvider.utils";
import { useAppointmentLabels } from "@/common/hooks/useAppointmentLabels";
import { useTranslation } from "@/translations";

import { ExchangeHistoryColumnWidth } from "../ExchangeHistoryPanel.utils";
import { ExchangeHistoryAppointmentCell } from "../components/ExchangeHistoryAppointmentCell";

type Row = AppointmentExchangeHistoryEntryResponse;

interface Params {
  serviceOptions: ColumnFilterOption[];
  givenServiceFilter: string | null;
  onGivenServiceFilterChange: (value: string | null) => void;
  receivedServiceFilter: string | null;
  onReceivedServiceFilterChange: (value: string | null) => void;
}

/** Column definitions for the appointment exchange (Tauschbörse) history: when, who gave which appointment
 *  away, and what came back. The two service columns carry a filter menu instead of sorting. Read-only — no
 *  action column. */
export function useExchangeHistoryColumns({
  serviceOptions,
  givenServiceFilter,
  onGivenServiceFilterChange,
  receivedServiceFilter,
  onReceivedServiceFilterChange,
}: Params): Array<GridColDef<Row>> {
  const { t } = useTranslation();
  const { language } = useLanguageContext();
  const labels = useAppointmentLabels();

  return useMemo<Array<GridColDef<Row>>>(() => {
    const locale = locales[language];

    return [
      {
        field: "acceptedAtUtc",
        headerName: t("exchange.history.columns.timestamp"),
        type: "dateTime",
        flex: 1,
        maxWidth: ExchangeHistoryColumnWidth.timestamp,
        renderCell: (params: GridRenderCellParams<Row>) => (
          <Stack justifyContent="center" sx={{ height: "100%" }}>
            <Typography variant="body2">{formatShortDate(params.row.acceptedAtUtc, locale)}</Typography>
            <Typography variant="caption" color="text.secondary">
              {formatTime(params.row.acceptedAtUtc, locale)} {t("common.clock")}
            </Typography>
          </Stack>
        ),
      },
      {
        field: "givenAwayByUserCode",
        headerName: t("exchange.history.columns.givenAwayBy"),
        flex: 1,
        maxWidth: ExchangeHistoryColumnWidth.counselorUserCode,
        sortable: false,
        renderCell: (params: GridRenderCellParams<Row>) => params.row.givenAwayByUserCode ?? EMPTY_VALUE_PLACEHOLDER,
      },
      {
        field: "givenStartTimeUtc",
        headerName: t("exchange.history.columns.givenAppointment"),
        type: "dateTime",
        flex: 1,
        minWidth: ExchangeHistoryColumnWidth.appointment,
        renderCell: (params: GridRenderCellParams<Row>) => (
          <ExchangeHistoryAppointmentCell
            startTimeUtc={params.row.givenStartTimeUtc}
            isAllDay={params.row.givenIsAllDay}
          />
        ),
      },
      {
        field: "givenPurposeKey",
        headerName: t("exchange.history.columns.givenService"),
        flex: 1,
        minWidth: ExchangeHistoryColumnWidth.purposeKey,
        sortable: false,
        renderHeader: () => (
          <ColumnFilterHeader
            label={t("exchange.history.columns.givenService")}
            options={serviceOptions}
            selectedValue={givenServiceFilter}
            onChange={onGivenServiceFilterChange}
          />
        ),
        renderCell: (params: GridRenderCellParams<Row>) => (
          <AppointmentPurposeChip label={labels.purposeLabelOf(params.row.givenPurposeKey)} />
        ),
      },
      {
        field: "exchangedWithUserCode",
        headerName: t("exchange.history.columns.exchangedWith"),
        flex: 1,
        maxWidth: ExchangeHistoryColumnWidth.counselorUserCode,
        sortable: false,
        renderCell: (params: GridRenderCellParams<Row>) => params.row.exchangedWithUserCode ?? EMPTY_VALUE_PLACEHOLDER,
      },
      {
        field: "receivedStartTimeUtc",
        headerName: t("exchange.history.columns.receivedAppointment"),
        type: "dateTime",
        flex: 1,
        minWidth: ExchangeHistoryColumnWidth.appointment,
        renderCell: (params: GridRenderCellParams<Row>) => (
          <ExchangeHistoryAppointmentCell
            startTimeUtc={params.row.receivedStartTimeUtc}
            isAllDay={params.row.receivedIsAllDay}
          />
        ),
      },
      {
        field: "receivedPurposeKey",
        headerName: t("exchange.history.columns.receivedService"),
        flex: 1,
        minWidth: ExchangeHistoryColumnWidth.purposeKey,
        sortable: false,
        renderHeader: () => (
          <ColumnFilterHeader
            label={t("exchange.history.columns.receivedService")}
            options={serviceOptions}
            selectedValue={receivedServiceFilter}
            onChange={onReceivedServiceFilterChange}
          />
        ),
        renderCell: (params: GridRenderCellParams<Row>) => (
          <AppointmentPurposeChip label={labels.purposeLabelOf(params.row.receivedPurposeKey)} />
        ),
      },
    ];
  }, [
    t,
    language,
    labels,
    serviceOptions,
    givenServiceFilter,
    onGivenServiceFilterChange,
    receivedServiceFilter,
    onReceivedServiceFilterChange,
  ]);
}
